import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Container,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  FormGroup,
  FormHelperText,
  Grid,
  Paper,
  Stack,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  Skeleton,
  Divider
} from '@mui/material';
import {
  Person,
  Mail,
  Phone,
  Group,
  Settings,
  Business,
  LocationOn,
  Source,
  Edit,
  WifiOff,
  Wifi,
  Warning
} from '@mui/icons-material';

// Import your services
import { getContact } from '../../service/Contacts/getContacts';
import { updateContact } from '../../service/Contacts/editContact';
import { getSuburbs } from '../../service/Address/getSuburbs';
import { getStates } from '../../service/Address/getStates'; 
import { getEnquirySource } from '../../service/Contacts/getenquirySource';
import { getContactTypes } from '../../service/Contacts/getContactTypes';
import { getSalesperson } from '../../service/User/getSalesperson';

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString();
};



const AlertUtils = {
  showSuccess: (title, message) => {
    alert(`${title}: ${message}`);
  },
  showError: (message) => {
    alert(`Error: ${message}`);
  }
};

const validationSchema = Yup.object().shape({
  firstNameContact: Yup.string().required('First Name is required'),
  lastNameContact: Yup.string().required('Last Name is required'),
});

const ContactEdit = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [states, setStates] = useState([]);
  const [suburbs, setSuburbs] = useState([]);
  const [contact, setContact] = useState(null);
  const [loadingSuburbs, setLoadingSuburbs] = useState(false);
  const [enquirySource, setEnquirySource] = useState([]);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [contactTypes, setContactTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const { contactId } = useParams();

  const fetchSuburbs = useCallback(async (stateId, stateName) => {
    if (!stateId || !stateName) {
      return [];
    }
    
    setLoadingSuburbs(true);
    try {
      const response = await getSuburbs(stateId, stateName);
      
      if (response && response.data) {
        setSuburbs(response.data);
        return response.data;
      } else {
        setSuburbs([]);
        return [];
      }
    } catch (error) {
      setSuburbs([]);
      return [];
    } finally {
      setLoadingSuburbs(false);
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    if (!contactId) return;

    setIsLoading(true);
    setError(null);
    try {
      const [contactResponse, statesResponse, enquirySourceResponse, contactTypesResponse, getUsers] = await Promise.all([
        getContact(contactId),
        getStates(contactId),
        getEnquirySource(),
        getContactTypes(),
        getSalesperson('contact_view')
      ]);

      const contactData = contactResponse.data;
    
      
      setContact(contactData);
      setStates(statesResponse.data);
      setEnquirySource(enquirySourceResponse.data);
      setContactTypes(contactTypesResponse.data);
      setUsers(getUsers.data);

      // Load suburbs if contact has state data
      if (contactData?.stateId && contactData?.state) {
        await fetchSuburbs(contactData.stateId, contactData.state);
      }
    } catch (error) {
     
      setError('Failed to load contact data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [contactId, fetchSuburbs]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleSubmit = async (values) => {
    setIsSavingContact(true);
    try {
      const response = await updateContact(values);
      if (response.status === 200 || response.status === 201) {
        AlertUtils.showSuccess('Edit Contact', 'Contact updated successfully');
        localStorage.setItem('editContact', 'true');
        await fetchAllData();
      }
    } catch (error) {
      
      setError('Failed to update contact. Please try again.');
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleContactTypeChange = (type, values, setFieldValue) => {
    let currentSelection = values.contactTypeID 
      ? values.contactTypeID.split(',').filter(id => id.trim() !== '') 
      : [];

    if (type.ContactTypeID.toString() === '6') {
      if (currentSelection.includes('6')) {
        currentSelection = currentSelection.filter(id => id !== '6');
      } else {
        currentSelection = ['6'];
      }
    } else {
      if (currentSelection.includes('6')) {
        currentSelection = currentSelection.filter(id => id !== '6');
      }

      if (currentSelection.includes(type.ContactTypeID.toString())) {
        currentSelection = currentSelection.filter(id => id !== type.ContactTypeID.toString());
      } else {
        currentSelection.push(type.ContactTypeID.toString());
      }
    }

    setFieldValue('contactTypeID', currentSelection.join(','));
  };

 
  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: 4 }}>
          <Stack spacing={3}>
            <Skeleton variant="text" width={300} height={40} />
            <Skeleton variant="rectangular" height={60} />
            <Skeleton variant="rectangular" height={200} />
            <Skeleton variant="rectangular" height={150} />
          </Stack>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={fetchAllData} variant="contained">
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        
        {/* Header */}
        <Box mb={4}>
          <Typography variant="h4" component="h1" gutterBottom>
            Edit Contact
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Update contact information and settings
          </Typography>
        </Box>

        <Formik
          enableReinitialize
          initialValues={{
            contactId: contact?.contactId || '',
            firstNameContact: contact?.firstNameContact || '',
            lastNameContact: contact?.lastNameContact || '',
            contactEmail: contact?.contactEmail || '',
            contactNumber: contact?.contactNumber || '',
            address: contact?.address || '',
            stateId: contact?.stateId || '',
            suburbId: contact?.suburbId || '',
            contactTypeID: contact?.contactTypeID || '',
            contactSourceId: contact?.contactSourceId || '',
            enableSMSBroadcast: contact?.enableSMSBroadcast || 0,
            enabledForBroadCast: contact?.enabledForBroadCast || 0,
            salespersonid: contact?.salespersonid || '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            handleChange,
            handleBlur,
            handleSubmit,
            values,
            errors,
            touched,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit}>
              <Stack spacing={4}>
                {/* Contact Details Section */}
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={3}>
                      <Person sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Contact Details</Typography>
                    </Box>
                    
                    <Grid container spacing={3}>
                      <Grid >
                        <TextField
                          fullWidth
                          label="First Name"
                          name="firstNameContact"
                          value={values.firstNameContact}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.firstNameContact && Boolean(errors.firstNameContact)}
                          helperText={touched.firstNameContact && errors.firstNameContact}
                          required
                        />
                      </Grid>
                      
                      <Grid >
                        <TextField
                          fullWidth
                          label="Last Name"
                          name="lastNameContact"
                          value={values.lastNameContact}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.lastNameContact && Boolean(errors.lastNameContact)}
                          helperText={touched.lastNameContact && errors.lastNameContact}
                          required
                        />
                      </Grid>
                      
                      <Grid >
                        <TextField
                          fullWidth
                          label="Email"
                          name="contactEmail"
                          type="email"
                          value={values.contactEmail}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.contactEmail && Boolean(errors.contactEmail)}
                          helperText={touched.contactEmail && errors.contactEmail}
                          slotProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Mail />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      
                      <Grid >
                        <TextField
                          fullWidth
                          label="Mobile Phone"
                          name="contactNumber"
                          value={values.contactNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.contactNumber && Boolean(errors.contactNumber)}
                          helperText={touched.contactNumber && errors.contactNumber}
                          slotProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Phone />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Enquiry Source Section */}
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={3}>
                      <Source sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Enquiry Source</Typography>
                    </Box>
                    
                    <Grid container spacing={3}>
                      <Grid >
                        <FormControl fullWidth>
                          <InputLabel>Enquiry Source</InputLabel>
                          <Select
                            value={values.contactSourceId}
                            label="Enquiry Source"
                            name="contactSourceId"
                            onChange={handleChange}
                          >
                            <MenuItem value="">Select Enquiry Source</MenuItem>
                            {enquirySource.map((source) => (
                              <MenuItem key={source.contactSourceId} value={source.contactSourceId}>
                                {source.source}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid >
                        <FormControl fullWidth>
                          <InputLabel>Salesperson</InputLabel>
                          <Select
                            value={values.salespersonid}
                            label="Salesperson"
                            name="salespersonid"
                            onChange={handleChange}
                          >
                            <MenuItem value="">Select Salesperson</MenuItem>
                            {users.map((user) => (
                              <MenuItem key={user.userid} value={user.userid}>
                                {user.salespersonName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Contact Type Section */}
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={3}>
                      <Group sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Contact Type</Typography>
                    </Box>
                    
                    <FormControl 
                      error={touched.contactTypeID && Boolean(errors.contactTypeID)}
                      component="fieldset"
                      fullWidth
                    >
                      <FormGroup>
                        <Grid container spacing={2}>
                          {contactTypes.map((type) => (
                            <Grid  md={4} key={type.ContactTypeID}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={values.contactTypeID.split(',').includes(type.ContactTypeID.toString())}
                                    onChange={() => handleContactTypeChange(type, values, setFieldValue)}
                                    disabled={values.contactTypeID.includes('6') && type.ContactTypeID.toString() !== '6'}
                                  />
                                }
                                label={type.Type}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </FormGroup>
                      {touched.contactTypeID && errors.contactTypeID && (
                        <FormHelperText>{errors.contactTypeID}</FormHelperText>
                      )}
                    </FormControl>
                  </CardContent>
                </Card>

                {/* Address Section */}
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={3}>
                      <LocationOn sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Address</Typography>
                    </Box>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Street Number and Address"
                          name="address"
                          value={values.address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.address && Boolean(errors.address)}
                          helperText={touched.address && errors.address}
                        />
                      </Grid>
                      
                      <Grid >
                        <FormControl fullWidth>
                          <InputLabel>State</InputLabel>
                          <Select
                            value={values.stateId}
                            label="State"
                            name="stateId"
                            onChange={async (e) => {
                              const selectedState = states.find(s => s.stateId === Number(e.target.value));
                              setFieldValue('stateId', e.target.value);
                              setFieldValue('suburbId', '');
                              if (selectedState) {
                                await fetchSuburbs(selectedState.stateId, selectedState.state);
                              } else {
                                setSuburbs([]);
                              }
                            }}
                          >
                            <MenuItem value="">Select State</MenuItem>
                            {states.map((state) => (
                              <MenuItem key={state.stateId} value={state.stateId}>
                                {state.state}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid >
                        <FormControl fullWidth disabled={loadingSuburbs || suburbs.length === 0}>
                          <InputLabel>Suburb</InputLabel>
                          <Select
                            value={values.suburbId}
                            label="Suburb"
                            name="suburbId"
                            onChange={handleChange}
                          >
                            <MenuItem value="">
                              {loadingSuburbs ? 'Loading suburbs...' : 
                               suburbs.length === 0 ? 'No suburbs available' : 
                               'Select Suburb'}
                            </MenuItem>
                            {suburbs.map((suburb) => (
                              <MenuItem key={suburb.suburbId ?? `${suburb.suburb}-${suburb.postcode}`} value={suburb.suburbId}>
                                {suburb.suburb}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Broadcast Options Section */}
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={3}>
                      <Settings sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Broadcast Options</Typography>
                    </Box>
                    
                    <Grid container spacing={3}>
                      <Grid >
                        <Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  contact?.DateUnsubscribedSMSBroadcast && contact.DateUnsubscribedSMSBroadcast !== ''
                                    ? false
                                    : values.enableSMSBroadcast === 1
                                }
                                onChange={(e) => setFieldValue('enableSMSBroadcast', e.target.checked ? 1 : 0)}
                                disabled={!!contact?.DateUnsubscribedSMSBroadcast}
                              />
                            }
                            label="SMS Broadcast"
                          />
                          {contact?.DateUnsubscribedSMSBroadcast && (
                            <Box mt={1}>
                              <Chip 
                                icon={<Warning />}
                                label={`Unsubscribed: ${formatDate(contact.DateUnsubscribedSMSBroadcast)}`}
                                color="warning"
                                size="small"
                              />
                            </Box>
                          )}
                        </Box>
                      </Grid>
                      
                      <Grid >
                        <Box>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={
                                  contact?.DateUnsubscribed && contact.DateUnsubscribed !== ''
                                    ? false
                                    : values.enabledForBroadCast === 1
                                }
                                onChange={(e) => setFieldValue('enabledForBroadCast', e.target.checked ? 1 : 0)}
                                disabled={!!contact?.DateUnsubscribed}
                              />
                            }
                            label="Email Broadcast"
                          />
                          {contact?.DateUnsubscribed && (
                            <Box mt={1}>
                              <Chip 
                                icon={<Warning />}
                                label={`Unsubscribed: ${formatDate(contact.DateUnsubscribed)}`}
                                color="warning"
                                size="small"
                              />
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Form Actions */}
                <Box display="flex" justifyContent="flex-end" gap={2} pt={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSavingContact}
                    size="large"
                    startIcon={isSavingContact ? <CircularProgress size={20} /> : <Edit />}
                  >
                    {isSavingContact ? 'Saving...' : 'Save Contact'}
                  </Button>
                </Box>
              </Stack>
            </form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
};

export default ContactEdit;